import type { History } from 'history'
import { equals, map, path, pathOr } from 'ramda'
import React from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import Loading from '@/app/component/guard/loading'
import Modal from '@/app/component/layout/modal'
import SelectAllNotification from '@/app/component/wrapper/table/selectable/notification/all'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import useSelectableTable from '@/app/hooks/use-selectable'
import SendMessageDialog, { SendMessageSelectionProps } from '@/app/module/common/component/send-message-dialog'
import AddToGroup, { AddToGroupProps } from '@/app/module/groups/component/add-to-group'
import RemoveFromGroup, { RemoveFromGroupProps } from '@/app/module/groups/component/remove-from-group'
import { RootState } from '@/app/store'
import { LoadingError, PersonalizationType } from '@/app/types'
import { Contact } from '@/app/types/contact'
import config from '@/config'
import { Column } from '@/config/types'

import { useGetGroupOptions } from '../../groups/hooks/use-get-group-options'
import { useGetSegmentOptions } from '../../segments/hooks/use-get-segment-options'
import DownloadDialogContent, { DownloadExtension } from './list/download-dialog-content'
import ContactsTable, {
  AddContactsToGroupProps,
  ContactsTableFilter,
  ContactsTableProps,
  ContactsTableQuery,
  RemoveContactsFromGroupProps,
} from './list/table/contacts-table'

type DeleteContactsPayload = Parameters<ContactsTableProps['deleteItems']>[0]

type AddToGroupState = Pick<
  AddToGroupProps,
  | 'totalRecipients'
  | 'recipients'
  | 'contactIds'
  | 'excludeIds'
  | 'isAllContacts'
  | 'includedGroupIds'
  | 'search'
  | 'groupIds'
>

type RemoveFromGroupState = Pick<
  RemoveFromGroupProps,
  'contactIds' | 'includedGroupIds' | 'recipients' | 'totalRecipients'
>

type Props = {
  activeFilter?: ContactsTableFilter
  addToGroupContacts?: string
  error?: LoadingError
  headers: Column[]
  history: History
  loading: boolean
  loadingItems: unknown[]
  orgId: number
  orgTotal: number
  ordered: number[]
  page: number
  pageSearchTerm?: string
  personalizationList?: PersonalizationType
  timezone: string
  token: string
  total: number
  values: Contact[]

  deleteItems: (payload: { token: string; orgId: number; itemIds: DeleteContactsPayload }) => Promise<void>
  downloadItems: (payload: {
    token: string
    orgId: number
    ext: DownloadExtension
    query: { group?: number[]; search?: string; ex?: number[]; id?: number[] }
  }) => Promise<void> | void
  getContacts: (payload: { token: string; orgId: number; query: ContactsTableQuery }) => Promise<void>
  getContactsTableHeaders: (orgId: number) => Column[]
  getCustomFields: (payload: { token: string; orgId: number }) => Promise<void>
  navigate: ContactsTableProps['navigate']
  saveContactsTableHeaders: (orgId: number, headers: Column[]) => void
  setPageSize: (size: number) => void
}

const defaultPageSize = Number(path(['modules', 'contacts', 'pageSize', 'default'], config) || 0)
const defaultFilter = { name: '', value: '' }

const Contacts: React.FC<Props> = (props) => {
  const {
    activeFilter,
    addToGroupContacts,
    error,
    headers,
    history,
    loading,
    loadingItems,
    orgId,
    orgTotal,
    ordered,
    page,
    pageSearchTerm,
    personalizationList = {},
    timezone,
    token,
    total,
    values,
    deleteItems,
    downloadItems,
    getContacts,
    getContactsTableHeaders,
    getCustomFields,
    navigate,
    saveContactsTableHeaders,
    setPageSize,
  } = props
  const createConfirmation = useConfirmation()
  const routerNavigate = useNavigate()
  const { isSelectAllNotificationOpen, selection, closeSelectAllNotification } = useSelectableTable({
    ordered,
    orgTotal,
    total,
  })

  const { data: groups } = useGetGroupOptions({ page: 1 })
  const { data: segments } = useGetSegmentOptions({ page: 1 })

  const filtersLoading = useSelector(({ groups, segments }: RootState) => groups.item.loading || segments.item.loading)
  const filters = React.useMemo(
    () => ({
      loading: filtersLoading,
      data: {
        groups: {
          type: 'radio',
          name: 'Groups',
          value: activeFilter && activeFilter.name === 'groups' ? activeFilter.value : '',
          values: groups,
        },
        segments: {
          type: 'radio',
          name: 'Segments',
          value: activeFilter && activeFilter.name === 'segments' ? activeFilter.value : '',
          values: segments,
        },
      },
    }),
    [activeFilter, groups, filtersLoading, segments],
  )

  const rawQuery = useSelector((state: RootState) => {
    const contacts = state.contacts
    return {
      page: Number(pathOr(1, ['list', 'data', 'page'], contacts)),
      size: Number(pathOr(defaultPageSize, ['list', 'data', 'size'], contacts)),
      searchTerm: pathOr('', ['list', 'data', 'search', 'active'], contacts),
      filter: pathOr(null, ['list', 'data', 'filter', 'active'], contacts),
    }
  }, shallowEqual)

  const [sendMessageModal, setSendMessageModal] = React.useState(false)
  const [addToGroupModal, setAddToGroupModal] = React.useState(false)
  const [removeFromGroupModal, setRemoveFromGroupModal] = React.useState(false)
  const [sendMessageProps, setSendMessageProps] = React.useState<SendMessageSelectionProps>(defaultSendMessageProps)
  const [addToGroupProps, setAddToGroupProps] = React.useState<AddToGroupState>(defaultAddToGroupProps)
  const [removeFromGroupProps, setRemoveFromGroupProps] =
    React.useState<RemoveFromGroupState>(defaultRemoveFromGroupProps)
  const localQueryRef = React.useRef<ContactsTableQuery>({
    page: 1,
    size: 0,
    searchTerm: '',
    filter: {
      name: '',
      value: '',
    },
  })
  const prevOrgIdRef = React.useRef<number | null>(null)

  const tableActiveFilter = activeFilter || defaultFilter

  const query = React.useMemo(() => {
    let filter: ContactsTableQuery['filter']
    if (activeFilter) {
      filter = activeFilter
    } else if (rawQuery.filter && typeof rawQuery.filter === 'object') {
      filter = rawQuery.filter
    } else {
      filter = defaultFilter
    }

    const nextQuery: ContactsTableQuery = {
      ...rawQuery,
      page,
      searchTerm: pageSearchTerm || rawQuery.searchTerm || '',
      filter,
    }

    if (equals(nextQuery, localQueryRef.current)) {
      return localQueryRef.current
    }

    localQueryRef.current = nextQuery
    return nextQuery
  }, [activeFilter, page, pageSearchTerm, rawQuery])

  React.useEffect(() => {
    if (addToGroupContacts) {
      setAddToGroupModal(true)
      setAddToGroupProps(convertPropToSelection(addToGroupContacts.split(',')))
    }
  }, [addToGroupContacts])

  React.useEffect(() => {
    if (prevOrgIdRef.current && prevOrgIdRef.current !== orgId) {
      selection.setAll(false)
      routerNavigate('/contacts')
    }
    prevOrgIdRef.current = orgId
  }, [orgId, selection, routerNavigate])

  const closeSendMessageModal = React.useCallback(() => {
    setSendMessageModal(false)
    setSendMessageProps(defaultSendMessageProps)
  }, [])

  const clearSelection = React.useCallback(() => {
    selection.setAll(false)
  }, [selection])

  const downloadExtensionRef = React.useRef<DownloadExtension>('xlsx')
  const downloadContacts = React.useCallback(() => {
    const downloadQuery: { group?: number[]; search?: string; ex?: number[]; id?: number[] } = {
      group: selection.groups,
      search: selection.search,
    }
    if (selection.isAll || selection.groups.length > 0 || selection.search !== '') {
      downloadQuery.ex = selection.list
    } else {
      downloadQuery.id = selection.list
    }

    downloadItems({
      token,
      orgId,
      ext: downloadExtensionRef.current,
      query: downloadQuery,
    })
  }, [orgId, selection, token, downloadItems])

  const handleSendMessage: ContactsTableProps['sendMessage'] = React.useCallback((props) => {
    setSendMessageModal(true)
    setSendMessageProps({
      ...props,
      recipients: props.recipients.map(({ label, value }) => ({ label, value: String(value) })),
    })
  }, [])

  const handleAddToGroup = React.useCallback((props: AddContactsToGroupProps) => {
    setAddToGroupModal(true)
    setAddToGroupProps({
      totalRecipients: props.totalRecipients,
      recipients: props.recipients.map(({ label, value }) => ({ label, value: String(value) })),
      contactIds: props.contactIds.map((id) => String(id)),
      excludeIds: props.excludeIds.map((id) => String(id)),
      isAllContacts: props.isAllContacts,
      includedGroupIds: props.includedGroupIds.map((id) => String(id)),
      search: props.search,
      groupIds: [],
    })
  }, [])

  const handleRemoveFromGroup = React.useCallback((props: RemoveContactsFromGroupProps) => {
    setRemoveFromGroupModal(true)
    setRemoveFromGroupProps({
      totalRecipients: props.totalRecipients,
      recipients: props.recipients.map(({ label, value }) => ({ label, value: String(value) })),
      contactIds: props.contactIds.map((id) => String(id)),
      includedGroupIds: props.includedGroupIds.map((id) => String(id)),
    })
  }, [])

  const handleDeleteItems = React.useCallback(
    async (ids: DeleteContactsPayload) => {
      const deleteLabel = selection.count > 1 ? `${selection.count} selected contacts` : 'selected contact'

      const confirmed = await createConfirmation({
        title: 'Delete Contacts',
        content: `Are you sure you want to delete the ${deleteLabel}?`,
        confirmButtonLabel: 'Delete',
        cancelButtonLabel: 'Cancel',
      })

      if (!confirmed) {
        return
      }

      await deleteItems({
        token,
        orgId,
        itemIds: ids,
      })

      await getContacts({
        token,
        orgId,
        query: {
          ...query,
          searchTerm: pageSearchTerm || '',
        },
      })

      selection.setAll(false)
    },
    [orgId, pageSearchTerm, query, selection, token, createConfirmation, deleteItems, getContacts],
  )

  const handleDownloadItems = React.useCallback(async () => {
    const confirmed = await createConfirmation({
      title: 'Download contacts',
      content: (
        <DownloadDialogContent
          onChange={({ value }) => {
            downloadExtensionRef.current = value as DownloadExtension
          }}
        />
      ),
      confirmButtonLabel: 'Download',
      cancelButtonLabel: 'Cancel',
    })

    if (!confirmed) {
      return
    }

    downloadContacts()
  }, [createConfirmation, downloadContacts])

  return (
    <>
      <SelectAllNotification
        isOpen={isSelectAllNotificationOpen}
        total={orgTotal}
        onClose={closeSelectAllNotification}
      />
      <Loading isLoading={loading} error={error}>
        <SendMessageDialog
          isOpen={sendMessageModal}
          personalizationList={personalizationList}
          sendMessageProps={sendMessageProps}
          onClearSelection={clearSelection}
          onClose={closeSendMessageModal}
        />
        <Modal
          icon="group_add"
          isOpen={addToGroupModal}
          title="Add Contacts to Groups"
          onClose={() => {
            setAddToGroupModal(false)
            navigate({
              ...query,
              history,
              page: 1,
              searchTerm: pageSearchTerm || '',
              filter: activeFilter || undefined,
            })
          }}
        >
          <AddToGroup
            activeFilter={activeFilter}
            {...addToGroupProps}
            closeHandler={() => {
              setAddToGroupModal(false)
              setAddToGroupProps(defaultAddToGroupProps)
              navigate({
                ...query,
                history,
                page: 1,
                searchTerm: pageSearchTerm || '',
                filter: activeFilter || undefined,
              })
            }}
            onAddToGroupSuccess={() => {
              selection.setAll(false)
              getContacts({
                token,
                orgId,
                query: {
                  ...query,
                  ...(pageSearchTerm && {
                    searchTerm: pageSearchTerm,
                  }),
                  ...(activeFilter?.value && {
                    filter: activeFilter,
                  }),
                },
              })
            }}
          />
        </Modal>
        <Modal
          icon="remove_circle"
          isOpen={removeFromGroupModal}
          title="Remove Contacts from Group"
          onClose={() => setRemoveFromGroupModal(false)}
        >
          <RemoveFromGroup
            selectedGroup={pathOr(undefined, ['value'], activeFilter)}
            {...removeFromGroupProps}
            closeHandler={() => {
              setRemoveFromGroupModal(false)
              setRemoveFromGroupProps(defaultRemoveFromGroupProps)
            }}
            onRemoveFromGroupSuccess={() => {
              selection.setAll(false)
            }}
          />
        </Modal>
        <ContactsTable
          activeFilter={tableActiveFilter}
          data={values}
          filters={filters}
          headers={headers}
          history={history}
          loading={loading}
          loadingItems={loadingItems}
          orgId={orgId}
          orgTotal={orgTotal}
          page={page}
          query={query}
          selection={selection}
          timezone={timezone}
          token={token}
          total={total}
          addContactsToGroup={handleAddToGroup}
          deleteItems={handleDeleteItems}
          downloadItems={handleDownloadItems}
          getContactsTableHeaders={getContactsTableHeaders}
          getCustomFields={getCustomFields}
          getData={getContacts}
          navigate={navigate}
          removeContactsFromGroup={handleRemoveFromGroup}
          saveContactsTableHeaders={saveContactsTableHeaders}
          sendMessage={handleSendMessage}
          setPageSize={setPageSize}
        />
      </Loading>
    </>
  )
}

const defaultSendMessageProps: SendMessageSelectionProps = {
  totalRecipients: 0,
  recipients: [],
  contactIds: [],
  excludeIds: [],
  isAllContacts: false,
  includedGroupIds: [],
  search: '',
}

const defaultAddToGroupProps: AddToGroupState = {
  totalRecipients: 0,
  recipients: [],
  contactIds: [],
  excludeIds: [],
  isAllContacts: false,
  includedGroupIds: [],
  search: '',
  groupIds: [],
}

const defaultRemoveFromGroupProps: RemoveFromGroupState = {
  totalRecipients: 0,
  recipients: [],
  contactIds: [],
  includedGroupIds: [],
}

const convertPropToSelection = (contacts: string[]): AddToGroupState => ({
  totalRecipients: contacts.length,
  recipients: map((contact) => ({ label: '', value: contact }), contacts),
  contactIds: map((contact) => String(contact), contacts),
  excludeIds: [],
  isAllContacts: false,
  includedGroupIds: [],
  search: '',
  groupIds: [],
})

export default Contacts
